import React from 'react';
import i18n from '../../app/i18n';

/**
 * Error Boundary component to catch JavaScript errors in child components.
 * Displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render shows the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Where it failed, by component, and never the error (1.6's 3.7). React prints the error itself as well, which
        // nothing here can stop (recorded in cmms decisions.md).
        console.error('ErrorBoundary caught an error:', errorInfo?.componentStack);
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
                    <div className="text-center max-w-md">
                        <svg
                            className="mx-auto h-16 w-16 text-error mb-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <h2 className="text-xl font-semibold text-base-content mb-2">
                            {i18n.t('errorBoundaryTitle')}
                        </h2>
                        <p className="text-base-content/70 mb-6">
                            {i18n.t('errorBoundaryBody')}
                        </p>
                        <div className="flex gap-2 justify-center">
                            <button
                                onClick={this.handleRetry}
                                className="btn btn-primary"
                            >
                                {i18n.t('errorBoundaryTryAgain')}
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn btn-ghost"
                            >
                                {i18n.t('errorBoundaryReload')}
                            </button>
                        </div>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-6 text-left">
                                <summary className="cursor-pointer text-sm text-base-content/50">
                                    Error Details (Development Only)
                                </summary>
                                <pre className="mt-2 p-4 bg-base-200 rounded-lg text-xs overflow-auto max-h-40">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
